/**
 * IndexedDB backend with normalized tables.
 * - Row-level operations instead of blobs
 * - Indexed queries (userId, listType)
 * - Bounded row size: one tournament per row in play_list
 */

import {
  DB_NAME,
  DB_VERSION,
  STORE_LIST_MEMBERS,
  STORE_PLAY_LIST,
  STORE_SETTINGS,
  STORE_USER_PREFS,
  type ListMemberRow,
  type ListMemberType,
  type PlayListRow,
  type SettingsRow,
  type UserPrefRow,
} from "./schema";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
        db.createObjectStore(STORE_SETTINGS, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORE_USER_PREFS)) {
        const prefs = db.createObjectStore(STORE_USER_PREFS, {
          keyPath: ["userId", "key"],
        });
        prefs.createIndex("userId", "userId", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_PLAY_LIST)) {
        const play = db.createObjectStore(STORE_PLAY_LIST, {
          keyPath: ["userId", "scheduleId"],
        });
        play.createIndex("userId", "userId", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_LIST_MEMBERS)) {
        const members = db.createObjectStore(STORE_LIST_MEMBERS, {
          keyPath: ["userId", "listType", "scheduleId"],
        });
        members.createIndex("userId_listType", ["userId", "listType"], {
          unique: false,
        });
      }
    };
  });
}

export interface GrindModeTableBackend {
  getSetting(key: string): Promise<string | null>;
  setSetting(key: string, value: string): Promise<void>;
  removeSetting(key: string): Promise<void>;
  getAllSettings(): Promise<Record<string, string>>;

  getUserPref(userId: number, key: string): Promise<string | null>;
  setUserPref(userId: number, key: string, value: string): Promise<void>;
  getAllUserPrefs(userId: number): Promise<Record<string, string>>;

  getPlayList(userId: number): Promise<PlayListRow[]>;
  setPlayList(userId: number, items: Array<Record<string, unknown>>): Promise<void>;

  getListIds(userId: number, listType: ListMemberType): Promise<number[]>;
  setListIds(userId: number, listType: ListMemberType, ids: number[]): Promise<void>;

  clear(): Promise<void>;
}

export class IndexedDBBackend implements GrindModeTableBackend {
  private db: IDBDatabase | null = null;
  private openPromise: Promise<IDBDatabase> | null = null;

  private async getDb(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (!this.openPromise) this.openPromise = openDB();
    this.db = await this.openPromise;
    return this.db;
  }

  private run<T>(
    fn: (db: IDBDatabase) => Promise<T>,
    mode: IDBTransactionMode = "readonly"
  ): Promise<T> {
    return this.getDb().then((db) => fn(db));
  }

  private tx(
    db: IDBDatabase,
    stores: string[],
    mode: IDBTransactionMode
  ): IDBTransaction {
    return db.transaction(stores, mode);
  }

  private req<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private txComplete(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getSetting(key: string): Promise<string | null> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_SETTINGS], "readonly");
      const store = tx.objectStore(STORE_SETTINGS);
      const row = await this.req(store.get(key));
      return row ? (row as { key: string; value: string }).value : null;
    });
  }

  async setSetting(key: string, value: string): Promise<void> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_SETTINGS], "readwrite");
      const store = tx.objectStore(STORE_SETTINGS);
      await this.req(store.put({ key, value }));
    });
  }

  async removeSetting(key: string): Promise<void> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_SETTINGS], "readwrite");
      const store = tx.objectStore(STORE_SETTINGS);
      await this.req(store.delete(key));
    });
  }

  async getAllSettings(): Promise<Record<string, string>> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_SETTINGS], "readonly");
      const store = tx.objectStore(STORE_SETTINGS);
      const rows = await this.req(store.getAll());
      const out: Record<string, string> = {};
      for (const r of rows as SettingsRow[]) {
        out[r.key] = r.value;
      }
      return out;
    });
  }

  async getUserPref(userId: number, key: string): Promise<string | null> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_USER_PREFS], "readonly");
      const store = tx.objectStore(STORE_USER_PREFS);
      const row = await this.req(store.get([userId, key]));
      return row ? (row as UserPrefRow).value : null;
    });
  }

  async setUserPref(userId: number, key: string, value: string): Promise<void> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_USER_PREFS], "readwrite");
      const store = tx.objectStore(STORE_USER_PREFS);
      await this.req(store.put({ userId, key, value }));
    });
  }

  async getAllUserPrefs(userId: number): Promise<Record<string, string>> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_USER_PREFS], "readonly");
      const store = tx.objectStore(STORE_USER_PREFS);
      const index = store.index("userId");
      const rows = await this.req(index.getAll(IDBKeyRange.only(userId)));
      const out: Record<string, string> = {};
      for (const r of rows as UserPrefRow[]) {
        out[r.key] = r.value;
      }
      return out;
    });
  }

  async getPlayList(userId: number): Promise<PlayListRow[]> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_PLAY_LIST], "readonly");
      const store = tx.objectStore(STORE_PLAY_LIST);
      const index = store.index("userId");
      const rows = await this.req(index.getAll(IDBKeyRange.only(userId)));
      return (rows as PlayListRow[]).sort((a, b) => a.addedAt - b.addedAt);
    });
  }

  async setPlayList(userId: number, items: Array<Record<string, unknown>>): Promise<void> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_PLAY_LIST], "readwrite");
      const store = tx.objectStore(STORE_PLAY_LIST);
      const index = store.index("userId");
      const existing = await this.req(index.getAll(IDBKeyRange.only(userId)));
      for (const r of existing as PlayListRow[]) {
        store.delete([r.userId, r.scheduleId]);
      }
      const now = Date.now();
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const scheduleId = (item.tournament_id ?? item.id) as number;
        store.add({
          userId,
          scheduleId,
          data: item,
          addedAt: now + i,
        });
      }
      await this.txComplete(tx);
    });
  }

  async getListIds(userId: number, listType: ListMemberType): Promise<number[]> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_LIST_MEMBERS], "readonly");
      const store = tx.objectStore(STORE_LIST_MEMBERS);
      const index = store.index("userId_listType");
      const rows = await this.req(
        index.getAll(IDBKeyRange.only([userId, listType]))
      );
      return (rows as ListMemberRow[]).map((r) => r.scheduleId);
    });
  }

  async setListIds(
    userId: number,
    listType: ListMemberType,
    ids: number[]
  ): Promise<void> {
    return this.run(async (db) => {
      const tx = this.tx(db, [STORE_LIST_MEMBERS], "readwrite");
      const store = tx.objectStore(STORE_LIST_MEMBERS);
      const index = store.index("userId_listType");
      const existing = await this.req(
        index.getAllKeys(IDBKeyRange.only([userId, listType]))
      );
      for (const key of existing as [number, string, number][]) {
        store.delete(key);
      }
      const seen = new Set<number>();
      for (const scheduleId of ids) {
        if (seen.has(scheduleId)) continue;
        seen.add(scheduleId);
        store.add({ userId, listType, scheduleId });
      }
      await this.txComplete(tx);
    });
  }

  async clear(): Promise<void> {
    return this.run(async (db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(
          [STORE_SETTINGS, STORE_USER_PREFS, STORE_PLAY_LIST, STORE_LIST_MEMBERS],
          "readwrite"
        );
        tx.objectStore(STORE_SETTINGS).clear();
        tx.objectStore(STORE_USER_PREFS).clear();
        tx.objectStore(STORE_PLAY_LIST).clear();
        tx.objectStore(STORE_LIST_MEMBERS).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    });
  }
}
